import React, { useState } from 'react';
import { FormOption } from '../types/api';
import { api } from '../services/api';
import { PATH_SEGMENTS } from '../config/paths';
import { formatTimeString } from '../utils/dateUtils';
import { getOptionScore, calculateQuestionScore } from '../utils/evaluationScoreUtils';
import { VHU_LOGO } from '../utils/iconMappings';
import QuestionHeader from './shared/QuestionHeader';
import QuestionOptions from './shared/QuestionOptions';
import '../styles/components/QuestionCard.css';

interface Evidence {
  id: number;
  analysis_config_id: number;
  start_time: string;
  end_time: string;
  evidence: string;
}

interface QuestionCardProps {
  id: string | number;
  questionNumber: number;
  questionText: string;
  questionType: string;
  optionType: string;
  options?: FormOption[];
  points: number;
  evaluationId: string;
  remarks: string;
  evidences?: Evidence[];
  onEvidenceClick?: (startTime: string) => void;
  prompt: string;
  moments?: {
    id: number;
    name: string;
    operation: string;
  }[];
  onRemarksChange: (questionId: string | number, remarks: string) => void;
  isEditable?: boolean;
  onEvaluationUpdate?: () => void;
  // Lifted state props
  pendingSelections: Record<number, boolean>;
  hasPendingChanges: boolean;
  reasonText: string;
  onPendingSelectionsChange: (questionId: string | number, selections: Record<number, boolean>) => void;
  onHasPendingChangesChange: (questionId: string | number, hasChanges: boolean) => void;
  onReasonTextChange: (questionId: string | number, reason: string) => void;
  onResetPendingChanges?: () => void;
  onRequestEditPermission?: (questionId: number) => boolean;
  onReleaseEditPermission?: (questionId: number) => void;
}

const QuestionCard: React.FC<QuestionCardProps> = ({
  id,
  questionNumber,
  questionText,
  questionType,
  optionType,
  options = [],
  points,
  evaluationId,
  remarks,
  evidences,
  onEvidenceClick,
  prompt,
  moments = [],
  onRemarksChange,
  isEditable = false,
  onEvaluationUpdate,
  // Lifted state props
  pendingSelections,
  hasPendingChanges,
  reasonText,
  onPendingSelectionsChange,
  onHasPendingChangesChange,
  onReasonTextChange,
  onResetPendingChanges,
  onRequestEditPermission,
  onReleaseEditPermission
}) => {
  const [editingRemarks, setEditingRemarks] = useState(false);
  const [localRemarks, setLocalRemarks] = useState(remarks);
  const [localPendingSelections, setLocalPendingSelections] = useState<Record<number, boolean> | null>(null);
  const [localReasonText, setLocalReasonText] = useState<string>('');

  React.useEffect(() => {
    if (hasPendingChanges && Object.keys(pendingSelections).length > 0) {
      clearLocalState();
    }
  }, [hasPendingChanges, pendingSelections]);

  // Check if local state matches server state
  const isLocalStateSameAsServer = (): boolean => {
    if (!localPendingSelections || !options || options.length === 0) {
      return true;
    }
    
    return options.every(option => {
      const localSelection = localPendingSelections[option.id] || false;
      const serverSelection = option.is_selected === 1;
      return localSelection === serverSelection;
    });
  };

  // Common function to clear local state
  const clearLocalState = () => {
    setLocalPendingSelections(null);
    setLocalReasonText('');
  };

  // Common function to reset parent state
  const resetParentState = () => {
    onHasPendingChangesChange(id, false);
    onReasonTextChange(id, '');
    onPendingSelectionsChange(id, {});
  };

  // Common function to request edit permission
  const requestEditPermission = (): boolean => {
    if (!isEditable) {
      return false;
    }

    if (onRequestEditPermission && !onRequestEditPermission(parseInt(id.toString()))) {
      alert("Another question is currently being edited. Please complete that editing first.");
      return false;
    }
    
    return true;
  };

  // Common function to initialize pending selections from current options
  const initializePendingSelections = (): Record<number, boolean> => {
    const newSelections: Record<number, boolean> = {};
    options.forEach(option => {
      newSelections[option.id] = option.is_selected === 1;
    });
    return newSelections;
  };

  // Common function to update parent state with new selections
  const updateParentState = (newSelections: Record<number, boolean>) => {
    onPendingSelectionsChange(id, newSelections);
    onHasPendingChangesChange(id, true);
  };

  // Function to determine if an option should be shown as selected
  const isOptionSelected = (option: FormOption): boolean => {
    // If we have local pending selections (during a change), use those
    if (localPendingSelections && localPendingSelections.hasOwnProperty(option.id)) {
      return localPendingSelections[option.id];
    }
    
    // If there are pending changes from parent, use pending selections
    if (hasPendingChanges && pendingSelections.hasOwnProperty(option.id)) {
      return pendingSelections[option.id];
    }
    
    return option.is_selected === 1;
  };

  // Function to calculate total question score from selected options
  const getQuestionScore = (): number => {
    if (!options || options.length === 0) return 0;
    
    // Create selectedOptionIds object for the utility function
    const selectedOptionIds: Record<number, boolean> = {};
    options.forEach(option => {
      selectedOptionIds[option.id] = isOptionSelected(option);
    });
    
    return calculateQuestionScore(options, selectedOptionIds);
  };

  // Calculate current question score and percentage
  const currentQuestionScore = getQuestionScore();
  const scorePercentage = points > 0 ? (currentQuestionScore / points) * 100 : 0;
  
  const getScoreColor = () => {
    if (scorePercentage === 100) return 'theme-success';
    if (scorePercentage === 0) return 'theme-danger';
    return 'theme-warning';
  };

  const getScoreText = () => {
    return `${currentQuestionScore}/${points}`;
  };

  // Function to get rating indicator elements between option text and score
  const getRatingIndicator = (option: FormOption): React.ReactElement | null => {
    if (option.rating_status === 'na') {
      return null; // No indicator for unrated options
    }
    
    // Check if this option is currently selected (including pending changes)
    const isCurrentlySelected = isOptionSelected(option);
    
    if (isCurrentlySelected) {
      // Option is selected - show system generated score indicator
      return (
        <div className="rating-indicator system-generated">
          <img src={VHU_LOGO} alt="VHU Logo" className="system-icon" />
        </div>
      );
    } else {
      // Option is not selected - show crossed system logo and crossed system score
      return (
        <div className="rating-indicator reviewer-override">
          <div className="system-score-crossed">
            <img src={VHU_LOGO} alt="VHU Logo" className="crossed-icon" />
            <span className="crossed-score">{option.points}</span>
          </div>
        </div>
      );
    }
  };

  const handleSaveRemarks = async () => {
    await handleRemarksChange(localRemarks);
    setEditingRemarks(false);
  };

  // API call to update evaluation options
  const updateEvaluationOptions = async (optionDataList: any[], reason: string) => {
    try {
      const payload = {
        eid: parseInt(evaluationId),
        option_data_list: optionDataList,
        reason: reason
      };
      
      const response = await fetch(api(PATH_SEGMENTS.EVALUATION, PATH_SEGMENTS.OPTIONS) + '/', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error Response:', response.status, errorText);
        throw new Error(`Failed to update evaluation options: ${response.status} ${errorText}`);
      }
      
      const responseData = await response.json();

      // Reset pending changes for the currently editing question
      if (onResetPendingChanges) {
        onResetPendingChanges();
      } else {
        // Fallback: Reset only current question if reset function not available
        resetParentState();
      }

      // Release edit permission after successful save
      if (onReleaseEditPermission) {
        onReleaseEditPermission(parseInt(id.toString()));
      }
      
      // Refresh evaluation data
      if (onEvaluationUpdate) {
        onEvaluationUpdate();
      }
    } catch (error) {
      console.error('Error updating evaluation options:', error);
      alert('Failed to save changes. Please try again.');
    }
  };

  // Handle save changes
  const handleSaveChanges = () => {
    if (!hasPendingChanges && !localPendingSelections) return;
    
    const optionDataList = options.map(option => ({
      eid: parseInt(evaluationId),
      qid: parseInt(id.toString()),
      opid: option.rated_option_id,
      is_selected: isOptionSelected(option) ? 1 : 0,
      score: getOptionScore(option)
    }));
    
    updateEvaluationOptions(optionDataList, localReasonText || reasonText);
  };

  // Handle cancel changes
  const handleCancelChanges = () => {
    // Clear local state immediately
    clearLocalState();
    
    // Update parent state
    resetParentState();

    // Release edit permission after cancel
    if (onReleaseEditPermission) {
      onReleaseEditPermission(parseInt(id.toString()));
    }
  };

  // Simplified option selection handler
  const handleOptionChange = (optionId: number) => {
    if (!requestEditPermission()) {
      return;
    }

    // Initialize pending selections from current options state
    let newSelections = initializePendingSelections();
    
    // Update pending selections based on option type
    if (optionType === 'MULTI_CHOICE') {
      // Toggle for multichoice
      newSelections[optionId] = !newSelections[optionId];
    } else {
      // Select only this option for YesNo/GRADING
      Object.keys(newSelections).forEach(id => newSelections[parseInt(id)] = false);
      newSelections[optionId] = true;
    }
    
    // Set local state immediately for UI feedback
    setLocalPendingSelections(newSelections);
    
    // Update parent state
    updateParentState(newSelections);
  };

  const handleScoreChange = (optionId: number, score: string) => {
    if (!requestEditPermission()) {
      return;
    }
    
    const numericScore = parseInt(score, 10);
    if (isNaN(numericScore)) return;
      
    // Initialize pending selections if first change
    let newSelections = { ...pendingSelections };
    if (!hasPendingChanges) {
      newSelections = {};
      options.forEach(option => {
        newSelections[option.id] = isOptionSelected(option);
      });
    }
    
    // Update parent state
    updateParentState(newSelections);
  };

  const handleRemarksChange = async (remarks: string) => {
    if (!requestEditPermission()) {
      return;
    }

    try {
      const response = await fetch(api(PATH_SEGMENTS.EVALUATION, PATH_SEGMENTS.QUESTION) + '/', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          eid: parseInt(evaluationId),
          qid: typeof id === 'string' ? parseInt(id) : id,
          remarks: remarks
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to update remarks: ${response.status}`);
      }

      // Trigger parent refresh to get updated data
      if (onEvaluationUpdate) {
        onEvaluationUpdate();
      }

      // Release edit permission
      if (onReleaseEditPermission) {
        onReleaseEditPermission(typeof id === 'string' ? parseInt(id) : id);
      }
    } catch (error) {
      console.error('Error updating remarks:', error);
    }
  };

  return (
    <div className="question-card">
      <div className="question-main-card">
        <QuestionHeader
          questionNumber={questionNumber}
          questionText={questionText}
          scoreText={getScoreText()}
          scoreColorClass={getScoreColor()}
        />
        
        <div className="card-content">
          <QuestionOptions
            optionType={optionType}
            options={options}
            questionId={id}
            moments={moments}
            isOptionSelected={isOptionSelected}
            onOptionChange={handleOptionChange}
            onScoreChange={handleScoreChange}
            getOptionScore={getOptionScore}
            getRatingIndicator={getRatingIndicator}
          />

          {(hasPendingChanges || (localPendingSelections && !isLocalStateSameAsServer())) && (
            <div className="pending-changes-section">
              <div className="reason-input-section">
                <input
                  id={`reason-${id}`}
                  type="text"
                  value={localReasonText || reasonText}
                  onChange={(e) => {
                    setLocalReasonText(e.target.value);
                    onReasonTextChange(id, e.target.value);
                  }}
                  placeholder="Enter reason for the change (optional)..."
                  className="reason-input"
                />
                <button 
                  className="btn btn-secondary btn-icon"
                  onClick={handleCancelChanges}
                  title="Cancel changes"
                >
                  ✕
                </button>
                <button 
                  className="btn btn-primary btn-icon"
                  onClick={handleSaveChanges}
                  title="Save changes"
                >
                  ✓
                </button>
              </div>
            </div>
          )}
                    
          {evidences && evidences.length > 0 && (
            <div className="evidence-section">
              <div className="evidence-list">
                {evidences.map((evidence, index) => (
                  <div 
                    key={index} 
                    className="evidence-item"
                    onClick={() => onEvidenceClick?.(evidence.start_time)}
                    role="button"
                    title="Click to play this segment"
                  >
                    <span className="material-symbols-outlined evidence-play-icon">sound_sampler</span>
                    <span className="evidence-time">{"[" + formatTimeString(evidence.start_time) + "]"}</span>
                    <span className="evidence-text">{evidence.evidence}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        
        <div className="card-footer">
          {editingRemarks ? (
            <div className="remarks-edit">
              <textarea
                value={localRemarks}
                onChange={(e) => setLocalRemarks(e.target.value)}
                placeholder="Enter question remarks..."
                className="remarks-textarea"
              />
              <div className="remarks-actions">
                <button 
                  className="btn btn-secondary"
                  onClick={() => setEditingRemarks(false)}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-primary"
                  onClick={handleSaveRemarks}
                >
                  Save
                </button>
              </div>
            </div>
          ) : (
            <div className="remarks-display">
              {remarks ? (
                <div className="remarks-text">{remarks}</div>
              ) : (
                <div className="remarks-placeholder">No remarks</div>
              )}
              {isEditable && (
                <button 
                  className="edit-remarks-btn"
                  onClick={() => setEditingRemarks(true)}
                  title="Edit remarks"
                >
                  <span className="material-icons">edit</span>
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuestionCard;
